import { ReactElement, ReactNode } from 'react';
import { DropdownArrow } from '@/shared/images/icons/DropdownArrow';

export type ScrollListProps<T> = {
    items: T[];
    selectedId?: string;
    getItemId: (item: T) => string;
    onSelect: (item: T) => void;
    renderItem: (item: T) => ReactNode;
    renderContent: (item: T) => ReactNode;
    ariaLabel: string;
};

export const ScrollList = <T,>({
    items,
    selectedId,
    getItemId,
    onSelect,
    renderItem,
    renderContent,
    ariaLabel,
}: ScrollListProps<T>): ReactElement => (
    <section className="flex w-full flex-col items-stretch justify-center lg:flex-row">
        <nav
            className="h-fit w-full overflow-hidden rounded-2xl bg-ash-normal px-2.5 py-6 dark:bg-blue-dark lg:mr-6 lg:w-80 lg:shrink-0"
            aria-label={ariaLabel}
        >
            <div
                className="custom-scroll max-h-262 overflow-x-hidden overflow-y-auto px-4"
                role="tablist"
                aria-label={ariaLabel}
            >
                {items.map((item) => {
                    const itemId = getItemId(item);
                    const isSelected = itemId === selectedId;

                    return (
                        <div
                            key={itemId}
                            className={`${
                                isSelected
                                    ? 'rounded-2xl bg-white-normal text-green-normal shadow-[0px_4px_23px_0px_rgba(189,189,189,0.23)] dark:bg-dark-3 dark:text-green-normal dark:shadow-[0px_4px_23px_0px_rgba(16,31,46,0.53)]'
                                    : 'bg-transparent text-gray-1 hover:text-green-normal dark:text-gray-3'
                            }`}
                        >
                            <button
                                type="button"
                                role="tab"
                                aria-selected={isSelected}
                                aria-controls={`scroll-list-content-${itemId}`}
                                onClick={() => onSelect(item)}
                                className="tx-medium-medium flex h-12 w-full cursor-pointer items-center justify-between rounded-2xl border-none bg-transparent px-5 text-left text-gray-1 transition-colors dark:text-gray-3"
                            >
                                {renderItem(item)}
                                <DropdownArrow
                                    className={`shrink-0 transition-transform lg:hidden ${
                                        isSelected ? 'rotate-180' : ''
                                    }`}
                                />
                            </button>
                            {isSelected && (
                                <div id={`scroll-list-content-${itemId}`} className="lg:hidden">
                                    {renderContent(item)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </nav>

        <div className="hidden min-w-0 flex-1 lg:block">
            {items.find((item) => getItemId(item) === selectedId) &&
                renderContent(items.find((item) => getItemId(item) === selectedId) as T)}
        </div>
    </section>
);
